/**
 * The picture of a file, at whatever size the caller gives it.
 *
 * One component for both views. The grid draws it square and large, the list
 * draws it at 40px, and neither decides for itself what a video or an
 * unrenderable file looks like - the two views used to answer that separately,
 * which is how the list ended up showing a bare extension where the grid showed
 * a first frame.
 */
import { Play } from "lucide-react";
import { cn } from "@wa/ui";
import type { MediaAsset } from "../../../lib/media";
import { extLabel, isImage, isVideo } from "./lib";

export function MediaThumb({
  asset,
  className,
  /** The grid's play badge is a filled disc; the list's is a bare glyph. */
  playMark = "disc",
}: {
  asset: MediaAsset;
  className?: string;
  playMark?: "disc" | "bare" | "none";
}) {
  if (isImage(asset)) {
    return (
      <img
        src={asset.url}
        alt={asset.originalFilename}
        loading="lazy"
        className={cn("size-full object-cover", className)}
      />
    );
  }

  if (isVideo(asset)) {
    return (
      <>
        {/* The first frame (#t=0.5) stands in for a poster image: asking the
            browser for metadata is cheaper than generating and storing one. */}
        <video
          src={`${asset.url}#t=0.5`}
          preload="metadata"
          muted
          playsInline
          className={cn("size-full object-cover", className)}
        />
        {playMark === "none" ? null : (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            {playMark === "disc" ? (
              // Hidden on hover, because the tile's own controls appear there.
              <span className="flex size-9 items-center justify-center rounded-full bg-black/55 text-white transition-opacity group-hover:opacity-0">
                <Play className="ml-0.5 size-4 fill-current" />
              </span>
            ) : (
              <Play className="size-3.5 fill-current text-white drop-shadow" />
            )}
          </span>
        )}
      </>
    );
  }

  return (
    <span className="flex size-full items-center justify-center bg-muted/30 px-1 text-center text-[10px] font-semibold text-muted-foreground">
      {extLabel(asset)}
    </span>
  );
}
